import React, { useState, useEffect, useCallback } from 'react';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import apiFetch, { safeJson, apiErrorDetail } from '../../../context/api';

/**
 * Gestión de ubicaciones de stock (almacén / neveras / armarios) — Fase B.
 * Backend: GET/POST/DELETE /v1/stock/locations
 */
const KIND_LABELS = {
  principal: 'Principal',
  nevera: 'Nevera',
  armario: 'Armario',
  sub_almacen: 'Sub-almacén',
};

const KIND_BADGE = {
  principal: 'bg-indigo-50 text-indigo-700',
  nevera: 'bg-sky-50 text-sky-700',
  armario: 'bg-amber-50 text-amber-700',
  sub_almacen: 'bg-slate-100 text-slate-600',
};

const StockLocationsModal = ({ token, venueID, onClose }) => {
  const overlayDismiss = useOverlayDismiss(onClose);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('nevera');
  const [saving, setSaving] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(venueID && { 'x-venue-id': venueID }),
  };

  const fetchLocations = useCallback(async () => {
    if (!venueID) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/v1/stock/locations?venue_id=${venueID}`, {
        headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) },
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cargar ubicaciones'));
      setLocations(data);
    } catch (e) {
      setError(e.message || 'Error al cargar ubicaciones');
    } finally {
      setLoading(false);
    }
  }, [venueID, token]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const createLocation = async () => {
    if (!name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch('/v1/stock/locations', {
        method: 'POST',
        headers,
        body: JSON.stringify({ venue_id: venueID, name: name.trim(), kind, is_sales_source: false }),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error al crear la ubicación'));
      setName('');
      setKind('nevera');
      await fetchLocations();
    } catch (e) {
      setError(e.message || 'Error al crear la ubicación');
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = async (loc) => {
    if (!window.confirm(`¿Borrar la ubicación "${loc.name}"?`)) return;
    setError('');
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) },
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error al borrar la ubicación'));
      await fetchLocations();
    } catch (e) {
      setError(e.message || 'Error al borrar la ubicación');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" {...overlayDismiss}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Cabecera */}
        <div className="flex items-start justify-between border-b border-[#E5E5E5] p-4">
          <div>
            <h3 className="text-base font-semibold text-[#0A0A0A]">Ubicaciones de stock</h3>
            <p className="mt-0.5 text-sm text-[#737373]">Almacén, neveras y armarios de esta tienda</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[#737373] hover:bg-[#F5F5F5]" aria-label="Cerrar">✕</button>
        </div>

        <div className="p-4">
          {error && <p className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-[#DC2626]">{error}</p>}

          {/* Lista */}
          <div className="mb-4 max-h-64 overflow-y-auto">
            {loading && <p className="py-6 text-center text-sm text-[#737373]">Cargando…</p>}
            {!loading && locations.length === 0 && <p className="py-6 text-center text-sm text-[#737373]">Sin ubicaciones.</p>}
            {!loading && locations.length > 0 && (
              <ul className="divide-y divide-[#E5E5E5]">
                {locations.map((loc) => (
                  <li key={loc.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-medium text-[#0A0A0A]">{loc.name}</span>
                      <span className={`ml-2 inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_BADGE[loc.kind] || KIND_BADGE.sub_almacen}`}>
                        {KIND_LABELS[loc.kind] || loc.kind}
                      </span>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {loc.kind !== 'principal' && (
                        <button onClick={() => deleteLocation(loc)} className="rounded px-2 py-1 text-xs font-medium text-[#DC2626] hover:bg-red-50">
                          Borrar
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Crear */}
          <div className="rounded-lg border border-[#E5E5E5] p-3 space-y-3">
            <p className="text-sm font-semibold text-[#0A0A0A]">Nueva ubicación</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="wa-form-field">
                <label className="wa-label">Nombre</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="wa-input" placeholder="Ej: Nevera barra" />
              </div>
              <div className="wa-form-field">
                <label className="wa-label">Tipo</label>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className="wa-input">
                  <option value="nevera">Nevera</option>
                  <option value="armario">Armario</option>
                  <option value="sub_almacen">Sub-almacén</option>
                </select>
              </div>
            </div>
            <button onClick={createLocation} disabled={saving} className="w-full rounded-md bg-lime-500 py-2.5 text-sm font-semibold text-white hover:bg-lime-600 disabled:opacity-60">
              {saving ? 'Creando…' : 'Crear ubicación'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockLocationsModal;
